import React, {useRef, useState} from 'react';
import {Button, Input, Space} from 'antd';
import {SearchOutlined} from '@ant-design/icons';
import type {ColumnType} from 'antd/es/table';
import type {FilterConfirmProps} from 'antd/es/table/interface';
import {EAssignmentKeyStatus, EUserSyncAction} from "../../../interfaces/user-sync.enums";


export const useColumnSearch = () => {
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');

    const searchInput = useRef<any>(null);

    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: any
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    // simple string highlighter using <mark>
    const highlightText = (text: string) => {
        if (!searchText) return text;
        const regex = new RegExp(`(${searchText})`, 'gi');
        const parts = text.split(regex);
        return (
            <>
                {parts.map((part, i) =>
                    part.toLowerCase() === searchText.toLowerCase() ? (
                        <mark key={i}>{part}</mark>
                    ) : (
                        <span key={i}>{part}</span>
                    )
                )}
            </>
        );
    };
    const getAssignmentKeyMessage = (input: EAssignmentKeyStatus, masterObject: any): string => {
        if (masterObject.action == EUserSyncAction.REMOVE && masterObject.actionStatus === 'Success') {
            return 'User Removed'
        } else {
            switch (input) {
                case EAssignmentKeyStatus.USED:
                    return 'Assignment key already used’'
                case EAssignmentKeyStatus.ACTIVE:
                    return 'Assignment key already exists and active, please check'
                case EAssignmentKeyStatus.NOT_EXIST:
                    return 'New Assignment Key Added'
                case EAssignmentKeyStatus.REACTIVATED:
                    return 'Existing assignment key has been reactivated'
                default:
                    return 'No Match found'
            }
        }
    }

    const getColumnSearchProps = (
        dataIndex: any,
        placeholder: string
    ): ColumnType<any> => ({
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => (
            <div style={{padding: 8}}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${placeholder}`}
                    value={selectedKeys[0]}
                    onChange={e =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    onPressEnter={() =>
                        handleSearch(selectedKeys as string[], confirm, dataIndex)
                    }
                    style={{marginBottom: 8, display: 'block'}}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() =>
                            handleSearch(selectedKeys as string[], confirm, dataIndex)
                        }
                        icon={<SearchOutlined/>}
                        size="small"
                        style={{
                            width: 90,
                            backgroundColor: '#5ab5df',
                            borderColor: '#5ab5df',
                            color: '#fff',
                        }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => {
                            clearFilters && handleReset(clearFilters);
                            confirm({closeDropdown: false});
                        }}
                        size="small"
                        style={{width: 90}}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        style={{color: '#5ab5df'}}
                        onClick={() => {
                            close();
                        }}
                    >
                        Close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{color: filtered ? '#1890ff' : undefined}}/>
        ),
        onFilter: (value, record: any) => {
            const searchVal = (value as string).toLowerCase();

            if (dataIndex === 'organizationMembers') {
                // ✅ Case 1: members exist
                if (record.organizationMembers?.length > 0) {
                    return record.organizationMembers.some((m: any) =>
                        m.member.name?.toLowerCase().includes(searchVal)
                    );
                }

                // ✅ Case 2: no members → search inside helper message
                const msg = getAssignmentKeyMessage(record.assignmentKeyStatus, record);
                return msg.toLowerCase().includes(searchVal);
            }

            // default for other fields
            return record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes(searchVal)
                : false;
        },
        render: (text: any, record: any) => {
            if (dataIndex === 'organizationMembers') {
                if (record.organizationMembers?.length > 0) {
                    return record.organizationMembers.map((m: any) => (
                        <div key={m._id}>{highlightText(m.member.name || 'No name')}</div>
                    ));
                } else {
                    const msg = getAssignmentKeyMessage(record.assignmentKeyStatus, record);
                    return highlightText(msg);
                }
            }

            return searchedColumn === dataIndex
                ? highlightText(text ? text.toString() : '')
                : text;
        },
    });

    return {getColumnSearchProps};
};
